import { BasketItem } from "../../../models/BasketItem";
import { TicketAccess } from "../../../models/TicketAccess";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import { GET_FOOD_LIST_URL } from "../../../api/apiRoutes";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../store";
import { useState } from "react";
import useFacility from "../../../../utils/hooks/useFacility";
import getId from "../../../../utils/getId";
import { Assort } from "../../../models/Assort";
import { AssortGroup } from "../../../models/AssortGroup";
import { setAddToBasketMessage } from "../../../store/alerts/alertsActions";
import useCustomHooks from "./useCustomHooks";
import { useTranslation } from "react-i18next";
import { errorMessageFromApi, NO_LOGGED_ERROR_MESSAGE } from "../../../utils/apiErrorMessages";
import {
  addBasketItem,
  addLimitedBasketItem,
  applyPromoCode,
  assignAssort,
  clearBasket as clearBasketAction,
  fetchBasket,
  prolongBasketPass,
  resetPromo,
  setBasketAddError,
  setPromoMessage,
} from "../../../store/basket/basketActions";

export { NO_LOGGED_ERROR_MESSAGE };

interface BasketAddResult {
  ok: boolean;
  error: string;
}

interface BasketManager {
  deletePromotions: Function;
  basket: BasketItem[];
  total: number;
  getBasketForSelectedSeats: Function;
  getBasket: Function;
  addAssortToBasket: Function;
  getFoodList: Function;
  handleAddBasket: Function;
  prolongPass: Function;
  handleAddBasketLimited: Function;
  handleGetBasket: Function;
  promoCode: Function;
  foods: Assort[];
  validateBasket: Function;
  promoMessage: string;
  currentPromoCode: string;
  totalListPrice: number;
  checkAdditionalBasket: Function;
  clearBasket: Function;
  isLoading: boolean;
  addError: string;
}

export default function useBasketManager(): BasketManager {
  const { t } = useTranslation();
  const { get } = useApiRequest();
  const facilityIds = useFacility();
  const dispatch = useDispatch();
  const [foods, setFoods] = useState<Assort[]>([]);
  const basket = useSelector((state: State) => state.basket.items);
  const total = useSelector((state: State) => state.basket.basketTotal);
  const totalListPrice = useSelector((state: State) => state.basket.totalListPrice);
  const promoMessage = useSelector((state: State) => state.basket.promoMessage);
  const currentPromoCode = useSelector((state: State) => state.basket.currentPromoCode);
  const isLoading = useSelector((state: State) => state.basket.loading);
  const addError = useSelector((state: State) => state.basket.addError);
  const popupAfterAddTicket = useSelector((state: State) => state.config.configuration.popupAfterAddTicket);
  const { addGuideToBasket } = useCustomHooks();

  const applyAddResult = (
    response: { isError?: boolean; errorTable?: string[] } | undefined,
    showPopup: boolean
  ): BasketAddResult => {
    if (!response?.isError) {
      dispatch(setBasketAddError(""));
      if (showPopup && popupAfterAddTicket === true) {
        dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
      }
      return { ok: true, error: "" };
    }
    const error = errorMessageFromApi(response);
    dispatch(setBasketAddError(error));
    return { ok: false, error };
  };

  const handleGetBasket = async () => {
    getBasket();
  };

  const getBasket = async (skipLoading = false) => {
    await dispatch(fetchBasket(skipLoading ? { skipLoading: true } : undefined));
  };

  const getBasketForSelectedSeats = (): BasketItem[] => {
    return basket;
  };

  const addAssortToBasket = async (assortIds: string, basketItem: BasketItem) => {
    await dispatch(assignAssort({ assortIds, basketItem }));
  };

  const getFoodList = async (activeGroup: AssortGroup) => {
    const { result, isError } = await get(GET_FOOD_LIST_URL(facilityIds, getId(activeGroup)));
    if (!isError) {
      setFoods(result);
    }
  };

  const handleAddBasket = async (
    assortIds: string,
    _assortName: string,
    price: number,
    ticketAccess?: TicketAccess | null,
    validFrom?: Date | string | null,
    qnt?: number
  ): Promise<BasketAddResult> => {
    const action: any = await dispatch(addBasketItem({ assortIds, price, ticketAccess, validFrom, qnt }));
    return applyAddResult(action?.payload, true);
  };

  const handleAddBasketLimited = async (assort: Assort, qnt: number, validFrom?: Date): Promise<BasketAddResult> => {
    const action: any = await dispatch(addLimitedBasketItem({ assort, qnt, validFrom }));
    return applyAddResult(action?.payload, true);
  };

  const prolongPass = async (assort: string, qnt: number, validFrom?: Date): Promise<BasketAddResult> => {
    const action: any = await dispatch(prolongBasketPass({ assort, qnt, validFrom }));
    return applyAddResult(action?.payload, facilityIds === "1200" || facilityIds === "1204");
  };

  const validateBasket = (): boolean => {
    return basket.some((item: BasketItem) => item.assortIds === "");
  };

  const promoCode = async (code: string) => {
    const action: any = await dispatch(applyPromoCode(code));
    if (!action?.payload?.isError) {
      dispatch(setPromoMessage(t("podany_kod_jest_poprawny")));
    } else {
      dispatch(setPromoMessage(errorMessageFromApi(action.payload)));
    }
  };

  const deletePromotions = async () => {
    await dispatch(resetPromo());
  };

  const checkAdditionalBasket = async (basketList: BasketItem[]) => {
    if (basketList.length > 0) {
      let assortName = "";
      let assortSecondName = "";
      let additionalAssortName = "";
      let additionalAssortId = 0;

      if (facilityIds === "1224") {
        assortName = "PRZEWODNIK GRUPY ZORGANIZOWANE ULGOWY OD 15 OS.";
        assortSecondName = "PRZEWODNIK GRUPY ZORGANIZOWANE OD 15 OS.";
        additionalAssortName = "PRZEWODNIK";
        additionalAssortId = 13;
      }

      if (!basketList.some((item: BasketItem) => item.assortName === additionalAssortName)) {
        if (
          basketList.some((item: BasketItem) => item.assortName === assortName) ||
          basketList.some((item: BasketItem) => item.assortName === assortSecondName)
        ) {
          addGuideToBasket(additionalAssortId, 1);
          setTimeout(() => {
            getBasket();
          }, 200);
        }
      }
      if (basketList.some((item: BasketItem) => item.assortName === additionalAssortName)) {
        if (
          !basketList.some((item: BasketItem) => item.assortName === assortName) &&
          !basketList.some((item: BasketItem) => item.assortName === assortSecondName)
        ) {
          addGuideToBasket(additionalAssortId, -1);
          setTimeout(() => {
            getBasket();
          }, 200);
        }
      }
    }
  };

  const clearBasket = async () => {
    await dispatch(clearBasketAction());
  };

  return {
    prolongPass,
    checkAdditionalBasket,
    deletePromotions,
    getBasket,
    promoMessage,
    promoCode,
    basket,
    total,
    totalListPrice,
    handleGetBasket,
    getBasketForSelectedSeats,
    addAssortToBasket,
    getFoodList,
    handleAddBasket,
    handleAddBasketLimited,
    foods,
    validateBasket,
    clearBasket,
    currentPromoCode,
    isLoading,
    addError,
  };
}
